import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useManagePaymentMethods } from '@features/payment/hooks/useManagePaymentMethods'

import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'

import Item from './Item'

import styles from './styles.module.scss'

type PaymentModalProps = {
  onChoose: (id: string) => Promise<void>;
  current: string | null;
};

export default function PaymentModal ({ onChoose, current }: PaymentModalProps) {
  const { paymentMethods, defaultCard } = useManagePaymentMethods()
  const { closeAllModals } = useModalStore()
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { trackEvent } = useAnalytics()
  const [ selected, setSelected ] = useState<string | null>(current)
  const [ isProcessing, setIsProcessing ] = useState(false)

  const handleChoose = async () => {
    const paymentId = selected || defaultCard?.id

    if (!paymentId) {
      openErrorInfoModal({
        description: te('payment_method_required') || 'Выберите способ оплаты',
      })
      return
    }

    setIsProcessing(true)

    try {
      trackEvent('select_payment_method', {
        payment_type: paymentId === 'new-card' ? 'new_card' : 'saved_card',
      })

      await onChoose(paymentId)

      closeAllModals()
    }
    catch (error) {
      console.error('[PaymentModal] Error selecting payment:', error)
      openErrorInfoModal({
        description: te('payment_selection_error') || 'Ошибка при выборе способа оплаты',
      })
    }
    finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.root__cards}>
        <Item
          key="new-card"
          data={null}
          isSelected={selected === 'new-card' || !paymentMethods.length}
          onSelect={() => setSelected('new-card')}
        />
        {paymentMethods.map((paymentMethod) => (
          <Item
            key={paymentMethod?.id}
            data={paymentMethod}
            isSelected={selected === paymentMethod?.id}
            onSelect={(id: string) => setSelected(id)}
          />
        ))}
      </div>
      <Button
        onClick={handleChoose}
        className={styles.root__button}
        size="large"
        disabled={isProcessing}
      >
        {isProcessing ? tc('loading') : tc('choose')}
      </Button>
    </div>
  )
}
